import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import BoardReportPages from "./document/BoardReportPages.jsx";
import CoverPage from "./document/CoverPage.jsx";
import ManifestPage from "./document/ManifestPage.jsx";
import useBoardReportDocument from "./useBoardReportDocument.js";
import { downloadRunArtifact } from "./document/boardReportApi.js";
import StatusPill from "../Budgets/components/StatusPill.jsx";
import { toast } from "../../components/ui-components/Toast.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
// Side-effect import: registers every flagship block type into blockRegistry
// before ManifestPage ever resolves a block by type (mirrors BoardReportPrint).
import "./blocks/index.js";
// Reused verbatim so the preview is pixel-identical to the printed PDF; its
// @page/print-mode rules are inert here since this view never sets the
// print-mode class on <html>.
import "./BoardReportPrint.css";
import "./BoardReportPreview.css";

const MIN_SCALE = 0.5;
const MAX_SCALE = 1.5;
// Horizontal padding (left+right) of .board-report-preview-canvas in
// BoardReportPreview.css - subtracted from the ResizeObserver-measured
// canvas width so "fit width" doesn't run the page edge-to-edge under the
// scrollbar/padding.
const CANVAS_HPAD = 32;
// A4 portrait height/width ratio (297mm / 210mm), used only to estimate each
// page's natural (unscaled) pixel height for anchor placement - the .a4-page
// rule sets a *min-height* of one A4 page, so this is an approximation, not
// a pixel-exact measurement per page.
const A4_ASPECT_RATIO = 297 / 210;
// Fixed-size thumb frame fallback (~0.12 scale of an A4 page at 96dpi:
// 210mm/297mm ≈ 794px/1123px * 0.12) used until a4PxWidth is measured, so the
// rail never collapses to zero width before the probe resolves.
const THUMB_SCALE = 0.12;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * usePagePagination - THE SP5 SEAM. Owns `activePageIndex` (0 = cover,
 * 1..N = manifest pages) and `scrollToPage(index)`, the only two things a
 * future guided-narration/deep-link feature (SP5, narrative-editing-with-
 * visuals) needs to drive page navigation from outside the toolbar/
 * thumbnail-rail UI: SP5 will call `scrollToPage(index)` to scroll this
 * canvas into sync with whatever page a narrative-editing focus/cursor is
 * on, and can read `activePageIndex` to reflect the canvas's current page
 * back into its own UI. `scrollToPage` clamps to [0, totalPages-1], updates
 * `activePageIndex`, and scrolls the matching page anchor into view
 * (registered via `registerPageAnchor`). `setActivePageIndex` is also
 * returned for the scroll-position-derived (IntersectionObserver) path,
 * which must NOT re-trigger a scroll.
 */
function usePagePagination(totalPages) {
  const [activePageIndex, setActivePageIndex] = useState(0);
  const pageAnchorRefs = useRef([]);

  const registerPageAnchor = useCallback(
    (index) => (node) => {
      pageAnchorRefs.current[index] = node;
    },
    []
  );

  const scrollToPage = useCallback(
    (index) => {
      const clamped = clamp(index, 0, Math.max(0, totalPages - 1));
      setActivePageIndex(clamped);
      const anchor = pageAnchorRefs.current[clamped];
      // jsdom (tests) has no scrollIntoView - guard rather than crash.
      if (anchor && typeof anchor.scrollIntoView === "function") {
        anchor.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [totalPages]
  );

  return { activePageIndex, setActivePageIndex, scrollToPage, registerPageAnchor, pageAnchorRefs };
}

/**
 * BoardReportPreview - the interactive, in-app preview of a paginated board
 * report run (/board-reports/:id/runs/:runId/preview), scaled to fit the
 * viewport with a zoom control, a page-navigation toolbar control, and a
 * left-hand thumbnail rail.
 *
 * Reuses BoardReportPages (the same cover+manifest-pages assembly
 * BoardReportPrint renders for PDF capture) so the on-screen preview is
 * pixel-identical to the printed document, wrapped in a scrollable canvas
 * that applies a CSS transform scale computed from either a measured
 * "fit width" ratio or a user-picked zoom factor.
 *
 * The thumbnail rail renders its own small clones of the SAME CoverPage/
 * ManifestPage components (not BoardReportPages, so each thumb is an
 * independently clickable nav target) at a fixed ~0.12 scale. Any figure
 * block a thumb's ManifestPage mounts fetches through useBlockData, which is
 * a react-query hook keyed by URL - the main page's identically-keyed
 * request is deduped/cached, so the rail never doubles API traffic.
 *
 * Read-only by design: every block renders print-static here (the same
 * static figure/table markup BoardReportPrint captures to PDF) - no chart
 * hover/tooltip/drill interactivity, no inputs, no contenteditable, no edit
 * affordances anywhere in this view. See `usePagePagination` above for the
 * SP5 (narrative-editing-with-visuals) seam this preview exposes.
 *
 * SP5 embed seams (all optional; absent -> byte-identical SP4/route
 * behavior, which is what BoardReportPrint and the plain route element
 * both still get):
 *  - `id`/`runId` props win over the route's `useParams()` values, so the
 *    two-pane editor can mount this component directly (outside the
 *    `/board-reports/:id/runs/:runId/preview` route) with explicit ids.
 *  - `narrativeOverride`/`driversOverride` are forwarded straight through
 *    to `BoardReportPages` as its `narrative`/`drivers` override props, so
 *    the editor can preview in-progress edits before they're persisted to
 *    `run`.
 *  - `focusPageId` scrolls the canvas to the manifest page with that id
 *    (once, on change) so the editor can keep the preview in sync with
 *    wherever the narrative-editing focus/cursor currently is.
 *  - `embedded` adds a modifier class so the shell fits a pane instead of
 *    assuming it owns the full viewport.
 *
 * SP6 seam: `selectable`/`selectedBlockId`/`onSelectBlock` are forwarded straight through to the
 * main canvas's `<BoardReportPages>` (not the thumbnail rail's own ManifestPage clones - the rail
 * stays a plain page-navigation control), so a contextual inspector can drive/react to block
 * selection on the report canvas. All optional; absent (every pre-SP6 caller, incl. the
 * `/board-reports/:id/runs/:runId/preview` route) renders every block bare, unchanged.
 */
const BoardReportPreview = ({
  id: idProp,
  runId: runIdProp,
  narrativeOverride,
  driversOverride,
  focusPageId,
  embedded,
  chromeless,
  selectable,
  selectedBlockId,
  selectedBinding,
  onSelectBlock,
}) => {
  const params = useParams();
  const id = idProp ?? params.id;
  const runId = runIdProp ?? params.runId;
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const hasToken = Boolean(keycloak.token);

  const { boardReport, run, manifest, isLoading, error, reload } = useBoardReportDocument(
    hasToken ? id : undefined,
    hasToken ? runId : undefined
  );

  // Downloads the FROZEN PDF artifact for this run - mirrors
  // BoardReportRunHistory's handleDownloadArtifact: fetch as a blob, wire it
  // up to a temporary <a download> so the browser saves it as a real file,
  // then release the object URL. Only reachable when run.artifact_id is set
  // (the toolbar button is disabled otherwise) - errors (e.g. an
  // expired/missing artifact) surface as a toast rather than a thrown
  // exception the user never sees.
  const handleDownloadArtifact = useCallback(async () => {
    try {
      const { blob, filename } = await downloadRunArtifact(id, runId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  }, [id, runId]);

  const manifestPages = manifest?.pages ?? [];
  const totalPages = 1 + manifestPages.length; // cover + manifest pages

  // The SP5 seam - see usePagePagination's doc comment above.
  const { activePageIndex, setActivePageIndex, scrollToPage, registerPageAnchor, pageAnchorRefs } =
    usePagePagination(totalPages);

  // SP5 seam: scroll to the manifest page identified by `focusPageId` (an
  // editor-driven focus/cursor position), once per change. Cover is page
  // index 0, so a manifest page found at array index N is page index N+1;
  // an absent or unmatched `focusPageId` (findIndex -> -1) intentionally
  // never scrolls, matching SP4's default (no `focusPageId` at all).
  useEffect(() => {
    if (!focusPageId) {
      return;
    }
    const found = manifestPages.findIndex((page) => page.id === focusPageId);
    const targetIdx = found >= 0 ? found + 1 : -1;
    if (targetIdx >= 0 && targetIdx !== activePageIndex) {
      scrollToPage(targetIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPageId, manifest]);

  // zoomMode: "fit" (default, scale = canvas width / natural A4 px width) |
  // "actual" (scale = 1) | a numeric factor (clamped to [MIN_SCALE, MAX_SCALE]).
  const [zoomMode, setZoomMode] = useState("fit");
  const [a4PxWidth, setA4PxWidth] = useState(0);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [contentNaturalHeight, setContentNaturalHeight] = useState(0);

  const probeRef = useRef(null);
  const canvasRef = useRef(null);
  const contentRef = useRef(null);

  // Measure the natural A4 pixel width once mm->px is resolved by the
  // browser (DPI-dependent, so this can't be a constant) - on mount and on
  // every window resize (a DPI/zoom change or just a viewport resize can
  // shift it).
  useLayoutEffect(() => {
    const measure = () => {
      if (probeRef.current) {
        setA4PxWidth(probeRef.current.getBoundingClientRect().width);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Track the canvas's own width (sidebar collapse/expand, window resize,
  // etc.) via ResizeObserver rather than only a window resize listener.
  // Depends on [isLoading, error] (not []): during the initial load the loading/error
  // branch renders WITHOUT the canvas, so a mount-only effect would find canvasRef.current
  // null, attach no observer, and never re-run once the canvas appears — leaving canvasWidth
  // at 0 and "fit" stuck at scale 1 (the page then overflows its pane). Re-running when the
  // canvas mounts, plus an immediate measure + a window-resize fallback, makes fit-width
  // reliable regardless of load timing.
  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) {
      return undefined;
    }
    const measure = () => setCanvasWidth(Math.max(0, canvasEl.clientWidth - CANVAS_HPAD));
    measure();
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setCanvasWidth(Math.max(0, entry.contentRect.width - CANVAS_HPAD));
        }
      });
      ro.observe(canvasEl);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, [isLoading, error]);

  // The unscaled content height, read from scrollHeight - a CSS transform
  // scale doesn't change scrollHeight, so this is safe to read directly off
  // the (already-scaled-by-transform) content element. Re-measure whenever
  // the document itself changes (different report/run loaded, or a
  // still-loading manifest resolves to more/fewer pages), OR a4PxWidth
  // changes (a mid-session DPI/resize shift changes the mm->px resolution
  // that .a4-page's width/min-height are defined in, which changes the
  // element's real rendered - and therefore natural scrollHeight - size).
  // scale itself is deliberately NOT a dependency: CSS transforms don't
  // affect layout/scrollHeight, so re-measuring on every zoom step would
  // just re-read the same number.
  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentNaturalHeight(contentRef.current.scrollHeight);
    }
  }, [boardReport, run, manifest, a4PxWidth]);

  let scale;
  if (zoomMode === "fit") {
    // Guard divide-by-zero / not-yet-measured probes (jsdom always reports
    // 0-width rects) so "fit" never produces NaN or Infinity - fall back to
    // 1 (actual size) until both dimensions are known.
    scale = a4PxWidth > 0 && canvasWidth > 0 ? canvasWidth / a4PxWidth : 1;
  } else if (zoomMode === "actual") {
    scale = 1;
  } else if (typeof zoomMode === "number") {
    scale = clamp(zoomMode, MIN_SCALE, MAX_SCALE);
  } else {
    scale = 1;
  }
  if (!Number.isFinite(scale) || scale <= 0) {
    scale = 1;
  }
  const roundedScale = Math.round(scale * 100) / 100;

  // Real-browser companion to the nav/thumb-driven path above: derive
  // activePageIndex from scroll position by observing which page anchor is
  // most in-view. Guarded for jsdom (no IntersectionObserver, no layout) -
  // there, activePageIndex is driven purely by scrollToPage (nav buttons /
  // thumbnail clicks setting state directly).
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const canvasEl = canvasRef.current;
    const anchors = pageAnchorRefs.current.filter(Boolean);
    if (!canvasEl || anchors.length === 0) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        let best = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting && (!best || entry.intersectionRatio > best.intersectionRatio)) {
            best = entry;
          }
        });
        if (best) {
          const index = pageAnchorRefs.current.indexOf(best.target);
          if (index !== -1) {
            setActivePageIndex(index);
          }
        }
      },
      { root: canvasEl, threshold: [0.5] }
    );
    anchors.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages, a4PxWidth, contentNaturalHeight]);

  const stepZoom = (delta) => {
    const current = Math.round(scale * 10) / 10;
    setZoomMode(clamp(Math.round((current + delta) * 10) / 10, MIN_SCALE, MAX_SCALE));
  };

  const canvasInnerStyle = {};
  if (a4PxWidth > 0) {
    canvasInnerStyle.width = a4PxWidth * scale;
  }
  if (contentNaturalHeight > 0) {
    canvasInnerStyle.height = contentNaturalHeight * scale;
  }

  // Per-page natural (unscaled) height estimate for anchor placement - see
  // A4_ASPECT_RATIO's doc comment.
  const pageHeightPx = a4PxWidth > 0 ? a4PxWidth * A4_ASPECT_RATIO : 0;

  const zoomBtnCls = (isActive) =>
    `board-report-preview-zoom-btn${isActive ? " active" : ""}`;

  // SP5 seam: `embedded` adds a modifier class so the shell fits inside a
  // pane (the two-pane editor) instead of assuming it owns the full
  // viewport - absent/false leaves the class list exactly as SP4's.
  // `chromeless` (SP6 editing-canvas fix) drops the reading chrome — the toolbar (page nav, zoom,
  // Download/Review) and the thumbnail rail — so the report canvas is just the clean, scrollable,
  // fit-width-scaled report with block selection, the way the review screen wants its primary
  // editing surface (matching the mockup). The standalone /preview route omits `chromeless`, so it
  // keeps the full reading experience. `chromeless` implies `embedded`'s pane-fit layout.
  const shellClassName =
    "board-report-preview" +
    (embedded ? " board-report-preview--embedded" : "") +
    (chromeless ? " board-report-preview--chromeless" : "");

  // Shared derivation, mirroring BoardReportPages' own - kept in sync so the
  // thumbnail rail's CoverPage/ManifestPage clones render identically to
  // the main canvas's.
  const title = boardReport?.name || "Board Report";
  const period = run?.period || boardReport?.period || "";
  const narrative = run?.narrative;
  const drivers = run?.drivers;
  const subtitle = boardReport?.subtitle;
  const confidentiality = boardReport?.confidentiality;
  const reportOwner = boardReport?.report_owner;

  const toolbar = (
    <div className="board-report-preview-toolbar">
      <div className="board-report-preview-toolbar-meta">
        <span className="board-report-preview-title">
          {boardReport?.name || "Board Report"}
        </span>
        {run?.period && <span className="board-report-preview-period">{run.period}</span>}
        <StatusPill status={run?.status} />
        <span className="board-report-preview-page-count">{totalPages} pages</span>
      </div>
      <div className="board-report-preview-page-nav" role="group" aria-label="Page navigation">
        <button
          type="button"
          aria-label="Previous page"
          className="board-report-preview-page-nav-btn"
          onClick={() => scrollToPage(activePageIndex - 1)}
          disabled={activePageIndex <= 0}
        >
          ◀
        </button>
        <span className="board-report-preview-page-indicator">
          {`Page ${activePageIndex + 1} / ${totalPages}`}
        </span>
        <button
          type="button"
          aria-label="Next page"
          className="board-report-preview-page-nav-btn"
          onClick={() => scrollToPage(activePageIndex + 1)}
          disabled={activePageIndex >= totalPages - 1}
        >
          ▶
        </button>
      </div>
      <div className="board-report-preview-zoom" role="group" aria-label="Zoom controls">
        <button
          type="button"
          className={zoomBtnCls(zoomMode === "fit")}
          onClick={() => setZoomMode("fit")}
        >
          Fit width
        </button>
        <button
          type="button"
          className={zoomBtnCls(zoomMode === "actual")}
          onClick={() => setZoomMode("actual")}
        >
          Actual size
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          className={`board-report-preview-zoom-btn board-report-preview-zoom-btn-icon`}
          onClick={() => stepZoom(-0.1)}
        >
          −
        </button>
        <span className="board-report-preview-zoom-pct">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          aria-label="Zoom in"
          className={`board-report-preview-zoom-btn board-report-preview-zoom-btn-icon`}
          onClick={() => stepZoom(0.1)}
        >
          +
        </button>
      </div>
      <div className="board-report-preview-actions" role="group" aria-label="Report actions">
        <button
          type="button"
          className={primaryButtonCls}
          onClick={handleDownloadArtifact}
          disabled={!run?.artifact_id}
          title={!run?.artifact_id ? "No exported PDF yet" : undefined}
        >
          Download PDF
        </button>
        <button
          type="button"
          className={secondaryButtonCls}
          onClick={() => navigate(`/board-reports/${id}/runs/${runId}/review`)}
        >
          Review
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={shellClassName}>
        {!chromeless && toolbar}
        <div className="board-report-preview-loading">Loading preview…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shellClassName}>
        {!chromeless && toolbar}
        <div className="board-report-preview-error">
          <div className="board-report-preview-error-card">
            <p>Failed to load board report: {error}</p>
            <button type="button" className={secondaryButtonCls} onClick={reload}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      {!chromeless && toolbar}
      <div className="board-report-preview-body">
        {!chromeless && (
        <nav className="board-report-preview-thumbnail-rail" aria-label="Page thumbnails">
          {Array.from({ length: totalPages }).map((_, index) => {
            const isActive = index === activePageIndex;
            return (
              <button
                key={index}
                type="button"
                aria-label={`Go to page ${index + 1}`}
                aria-current={isActive ? "true" : undefined}
                className={`board-report-preview-thumb${isActive ? " active" : ""}`}
                onClick={() => scrollToPage(index)}
              >
                <div className="board-report-preview-thumb-frame">
                  <div
                    className="board-report-preview-thumb-scaler"
                    style={{ transform: `scale(${THUMB_SCALE})` }}
                  >
                    {index === 0 ? (
                      <CoverPage
                        title={title}
                        subtitle={subtitle}
                        confidentiality={confidentiality}
                        reportOwner={reportOwner}
                        period={period}
                        generatedAt={new Date().toISOString()}
                      />
                    ) : (
                      <ManifestPage
                        page={manifestPages[index - 1]}
                        period={period}
                        pageNumber={index + 1}
                        totalPages={totalPages}
                        boardReportId={id}
                        runId={runId}
                        narrative={narrative}
                        drivers={drivers}
                      />
                    )}
                  </div>
                </div>
                <span className="board-report-preview-thumb-number">{index + 1}</span>
              </button>
            );
          })}
        </nav>
        )}
        <div className="board-report-preview-canvas" ref={canvasRef}>
          {/* Hidden probe used only to measure 210mm in resolved CSS px (DPI-dependent). */}
          <div className="board-report-preview-probe" ref={probeRef} />
          <div
            className="board-report-preview-canvas-inner"
            data-preview-scale={String(roundedScale)}
            style={canvasInnerStyle}
          >
            {/* Invisible per-page scroll anchors, owned directly by this
                component (not BoardReportPages) so scrollToPage always has a
                real DOM node to call scrollIntoView on regardless of how
                BoardReportPages lays its own children out. Positioned via
                the pageHeightPx estimate - see A4_ASPECT_RATIO's comment. */}
            {Array.from({ length: totalPages }).map((_, index) => (
              <div
                key={index}
                className="board-report-preview-page-anchor"
                data-page-index={index}
                ref={registerPageAnchor(index)}
                style={{ top: `${index * pageHeightPx * scale}px` }}
              />
            ))}
            <div
              className="board-report-preview-content"
              ref={contentRef}
              style={{ transform: `scale(${scale})` }}
            >
              <BoardReportPages
                boardReport={boardReport}
                run={run}
                manifest={manifest}
                boardReportId={id}
                runId={runId}
                narrative={narrativeOverride}
                drivers={driversOverride}
                selectable={selectable}
                selectedBlockId={selectedBlockId}
                selectedBinding={selectedBinding}
                onSelectBlock={onSelectBlock}
                hideCover={chromeless}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BoardReportPreview.propTypes = {
  // SP5 embed seams - all optional; see the component doc comment above.
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  runId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  narrativeOverride: PropTypes.object,
  driversOverride: PropTypes.arrayOf(PropTypes.object),
  focusPageId: PropTypes.string,
  embedded: PropTypes.bool,
  chromeless: PropTypes.bool,
  // SP6 seam - see the component doc comment above.
  selectable: PropTypes.bool,
  selectedBlockId: PropTypes.string,
  selectedBinding: PropTypes.object,
  onSelectBlock: PropTypes.func,
};

export default BoardReportPreview;
